import type { InsightsMetricValue } from "constants/constants";
import type {
  Granularity,
  KpiBreakdown,
} from "@lib/api-hooks/useKpiBreakdownForChannel/use-kpi-breakdown-for-channel";
import {
  FilterTypesEnum,
  CHANNELS_SUPPORTING_BREAKDOWN,
  TOUCHPOINT_OPTIONS,
} from "@tracify/shared";

// Layout types for react-grid-layout (matching their type definitions)
export type ResizeHandle = "s" | "w" | "e" | "n" | "sw" | "nw" | "se" | "ne";

export interface Layout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  moved?: boolean;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
  resizeHandles?: readonly ResizeHandle[];
}

export interface Layouts {
  [P: string]: Layout[];
}

// Grid configuration constants
// Using 6 columns allows: full (6), 1/2 (3), 1/3 (2), 2/3 (4)
// Below ~1150px container width (approx 1350px viewport with sidebar), use single column
export const GRID_CONFIG = {
  breakpoints: { lg: 1150, sm: 0 },
  cols: { lg: 6, sm: 1 },
  rowHeight: 150,
  margin: [16, 16] as [number, number],
};

// Default layout dimensions for new tiles (default to half-width)
export const DEFAULT_LAYOUT_ITEM = {
  w: 3, // Half width on lg (6 cols), 3/4 on md (4 cols), full on sm (2 cols)
  h: 3, // Each h unit = 150px, so h=3 is 450px (increased from h=2 for better chart visibility)
  minW: 2, // Minimum 1/3 width on lg
  maxW: 6, // Maximum full width
  minH: 2, // Allow shrinking back to 2 if needed
  maxH: 8, // Allow more vertical expansion
};

// Responsive layouts structure
export interface ResponsiveInsightLayouts {
  lg: Layout[];
  sm: Layout[];
}

// Customer type for data extraction (total, new, returning)
export type CustomerType = "total" | "new" | "returning";

// Breakdown dimension options
export type BreakdownDimension =
  | "channel"
  | "campaign"
  | "adset"
  | "ad"
  | "creative";

// Sort order type for breakdown
export type BreakdownSortOrder = "asc" | "desc";

// Breakdown configuration for a series
export interface BreakdownConfig {
  dimension: BreakdownDimension;
  limit: number; // Default 25
  sortBy: InsightsMetricValue; // Metric to sort by (default: first series metric)
  sortOrder: BreakdownSortOrder; // Default "desc"
}

export const INSIGHT_BREAKDOWN_DEFAULT_LIMIT = 25;
export const INSIGHT_BREAKDOWN_MAX_LIMIT = 25;

export function clampInsightBreakdownLimit(
  limit: number | null | undefined
): number {
  if (!Number.isFinite(limit)) {
    return INSIGHT_BREAKDOWN_DEFAULT_LIMIT;
  }

  return Math.min(
    Math.max(1, Math.floor(limit as number)),
    INSIGHT_BREAKDOWN_MAX_LIMIT
  );
}

// Filterable field names
export type FilterableName =
  | "channel"
  | "customer_type"
  | "campaign_id"
  | "campaign_name"
  | "adset_id"
  | "adset_name"
  | "ad_id"
  | "ad_name";

// Filter configuration
export interface InsightFilterConfig {
  id: string; // Unique ID for this filter
  name: FilterableName;
  operator: FilterTypesEnum;
  value: string | number | (string | number)[];
}

// Filter operator for combining multiple filters (AND/OR)
export type FilterOperator = "and" | "or";

// Breakdown dimension options for UI
export const BREAKDOWN_DIMENSION_OPTIONS: {
  label: string;
  value: BreakdownDimension;
}[] = [
  { label: "Channel", value: "channel" },
  { label: "Campaign", value: "campaign" },
  { label: "Adset", value: "adset" },
  { label: "Ad", value: "ad" },
  { label: "Creative", value: "creative" },
];

// Filter field options for UI
export const FILTER_FIELD_OPTIONS: {
  label: string;
  value: FilterableName;
  operators: FilterTypesEnum[];
}[] = [
  {
    label: "Channel",
    value: "channel",
    // Channel uses multi-select only (no operator dropdown shown in UI)
    operators: [FilterTypesEnum.in],
  },
  {
    label: "Customer type",
    value: "customer_type",
    operators: [FilterTypesEnum.eq],
  },
  {
    label: "Campaign ID",
    value: "campaign_id",
    operators: [
      FilterTypesEnum.eq,
      FilterTypesEnum.ne,
      FilterTypesEnum.icontains,
      FilterTypesEnum.not_icontains,
    ],
  },
  {
    label: "Campaign Name",
    value: "campaign_name",
    operators: [
      FilterTypesEnum.eq,
      FilterTypesEnum.ne,
      FilterTypesEnum.icontains,
      FilterTypesEnum.not_icontains,
    ],
  },
  {
    label: "Adset ID",
    value: "adset_id",
    operators: [
      FilterTypesEnum.eq,
      FilterTypesEnum.ne,
      FilterTypesEnum.icontains,
      FilterTypesEnum.not_icontains,
    ],
  },
  {
    label: "Adset Name",
    value: "adset_name",
    operators: [
      FilterTypesEnum.eq,
      FilterTypesEnum.ne,
      FilterTypesEnum.icontains,
      FilterTypesEnum.not_icontains,
    ],
  },
  {
    label: "Ad ID",
    value: "ad_id",
    operators: [
      FilterTypesEnum.eq,
      FilterTypesEnum.ne,
      FilterTypesEnum.icontains,
      FilterTypesEnum.not_icontains,
    ],
  },
  {
    label: "Ad Name",
    value: "ad_name",
    operators: [
      FilterTypesEnum.eq,
      FilterTypesEnum.ne,
      FilterTypesEnum.icontains,
      FilterTypesEnum.not_icontains,
    ],
  },
];

// Filter operator labels for UI
export const FILTER_OPERATOR_LABELS: Record<FilterTypesEnum, string> = {
  [FilterTypesEnum.eq]: "equals",
  [FilterTypesEnum.ne]: "not equals",
  [FilterTypesEnum.in]: "is one of",
  [FilterTypesEnum.icontains]: "contains",
  [FilterTypesEnum.not_icontains]: "does not contain",
  [FilterTypesEnum.gt]: "greater than",
  [FilterTypesEnum.lt]: "less than",
  [FilterTypesEnum.in_at]: "in at",
  [FilterTypesEnum.contains_any]: "contains any",
};

// Breakdown limit options
export const BREAKDOWN_LIMIT_OPTIONS = [
  { label: "25", value: INSIGHT_BREAKDOWN_DEFAULT_LIMIT },
];

// Breakdown sort order options
export const BREAKDOWN_SORT_ORDER_OPTIONS: {
  label: string;
  value: BreakdownSortOrder;
}[] = [
  { label: "Descending", value: "desc" },
  { label: "Ascending", value: "asc" },
];

// Chart types available for insights
export type InsightChartType =
  // Time Series
  | "line"
  | "area"
  | "bar"
  | "stacked_bar"
  // Cumulative Time Series
  | "line_cumulative"
  // Total Value
  | "number"
  | "pie"
  | "bar_horizontal";

// InsightType includes chart types plus table view
export type InsightType = InsightChartType | "table";

// Helper to check if type is table
export const isTableType = (type: InsightType): boolean => type === "table";

// Time range preset values
export type TimeRangePreset =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_14_days"
  | "last_30_days"
  | "last_90_days"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "year_to_date"
  | "last_custom_days";

// Comparison period types (matches PREVIOUS_DATE_RANGE_SELECT_OPTIONS without custom)
export type ComparisonPeriod =
  | "no_comparison"
  | "last_period"
  | "last_week"
  | "last_month"
  | "last_quarter"
  | "last_year";

// Y-axis unit types for value formatting
export type YAxisUnitType =
  | "none"
  | "duration_s"
  | "duration_ms"
  | "percent_1"
  | "currency"
  | "custom_prefix"
  | "custom_postfix";

// Legacy type - kept for backward compatibility during migration
export type InsightViewMode = "chart" | "table";

// Individual series configuration
export interface InsightSeries {
  id: string;
  label: string; // A, B, C, etc.
  metric: InsightsMetricValue;
  filters?: InsightFilterConfig[];
  color: string;
}

// Complete insight configuration
export interface InsightConfig {
  id: string;
  title: string;
  description?: string;

  // Series (multiple per insight)
  series: InsightSeries[];

  // Shared filters & breakdown (applies to all series)
  filters: InsightFilterConfig[];
  filterOperator: FilterOperator; // How to combine filters (default: "and")
  breakdown: BreakdownConfig[]; // Array of breakdowns (currently only first is used)

  // Independent time settings per insight
  timeRange: {
    preset: TimeRangePreset;
    customDays?: number; // Used when preset is "last_custom_days"
  };

  // Controls whether insight uses its own time range on dashboards
  // When true, insight ignores dashboard-level time range
  // When false/undefined (default), inherits from dashboard context
  useOwnTimeRange?: boolean;

  // Granularity setting
  granularity: Granularity;

  // Comparison settings
  comparisonPeriod: ComparisonPeriod;

  // Insight-specific attribution preset.
  // Must always be present as a string. Empty string means "not selected yet".
  attributionPreset: string;

  // Insight type (chart type or table)
  type: InsightType;

  // Display options for chart visualization
  displayOptions?: {
    showLegend: boolean;
    showMultipleYAxes: boolean;
    showSummaryStats: boolean;
    // Y-axis unit formatting (moved from top-level)
    yAxisUnit?: {
      type: YAxisUnitType;
      customValue?: string;
    };
  };

  // Metadata
  schemaVersion: number;
  createdAt: number;
  updatedAt: number;
}

// Series label generation (A-H)
export const SERIES_LABELS = ["A", "B", "C", "D", "E", "F", "G", "H"];

// Series colors palette (PostHog-inspired)
export const SERIES_COLORS = [
  "#22c55e", // green
  "#8b5cf6", // purple
  "#f97316", // orange
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#eab308", // yellow
  "#14b8a6", // teal
  "#6366f1", // indigo
];

// Customer type options for dropdowns
export const CUSTOMER_TYPE_OPTIONS: { label: string; value: CustomerType }[] = [
  { label: "Total", value: "total" },
  { label: "New", value: "new" },
  { label: "Returning", value: "returning" },
];

const VALID_CUSTOMER_TYPE_VALUES = new Set<CustomerType>(
  CUSTOMER_TYPE_OPTIONS.map((option) => option.value)
);

function normalizeInsightFilterScalarValue(
  value: string | number | null | undefined
): string | number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  return null;
}

function normalizeInsightFilterArrayValue(
  value: string | number | (string | number)[]
): (string | number)[] {
  if (!Array.isArray(value)) {
    const normalizedValue = normalizeInsightFilterScalarValue(value);
    return normalizedValue === null ? [] : [normalizedValue];
  }

  return value.reduce<(string | number)[]>((acc, item) => {
    const normalizedItem = normalizeInsightFilterScalarValue(item);

    if (normalizedItem !== null) {
      acc.push(normalizedItem);
    }

    return acc;
  }, []);
}

export function isInsightFilterConfigured(
  filter: InsightFilterConfig
): boolean {
  if (filter.name === "customer_type") {
    const rawValue = Array.isArray(filter.value)
      ? filter.value[0]
      : filter.value;
    return VALID_CUSTOMER_TYPE_VALUES.has(
      String(rawValue ?? "")
        .trim()
        .toLowerCase() as CustomerType
    );
  }

  if (filter.operator === FilterTypesEnum.in || filter.name === "channel") {
    return normalizeInsightFilterArrayValue(filter.value).length > 0;
  }

  const rawValue = Array.isArray(filter.value) ? filter.value[0] : filter.value;
  return normalizeInsightFilterScalarValue(rawValue) !== null;
}

export function getConfiguredInsightFilters(
  filters: InsightFilterConfig[] | undefined
): InsightFilterConfig[] {
  return (filters ?? []).filter(isInsightFilterConfigured);
}

export function hasUnconfiguredInsightFilters(
  filters: InsightFilterConfig[] | undefined
): boolean {
  return (filters ?? []).some((filter) => !isInsightFilterConfigured(filter));
}

// Display mode includes chart types plus table view
export type InsightDisplayMode = InsightChartType | "table";

// Chart type option
export interface ChartTypeOption {
  label: string;
  value: InsightChartType;
  description: string;
}

// Display mode option (includes table)
export interface DisplayModeOption {
  label: string;
  value: InsightDisplayMode;
  description: string;
}

// Display mode group
export interface DisplayModeGroup {
  label: string;
  options: DisplayModeOption[];
}

// Chart type group (legacy, for backward compatibility)
export interface ChartTypeGroup {
  label: string;
  options: ChartTypeOption[];
}

// Grouped chart type options for dropdowns
export const CHART_TYPE_GROUPS: ChartTypeGroup[] = [
  {
    label: "Time Series",
    options: [
      {
        label: "Line chart",
        value: "line",
        description: "Trends over time as a continuous line",
      },
      {
        label: "Area chart",
        value: "area",
        description: "Trends over time as a shaded area",
      },
      {
        label: "Bar chart",
        value: "bar",
        description: "Trends over time as vertical bars side-by-side",
      },
      {
        label: "Stacked bar chart",
        value: "stacked_bar",
        description: "Trends over time as stacked vertical bars",
      },
    ],
  },
  {
    label: "Cumulative Time Series",
    options: [
      {
        label: "Line chart (cumulative)",
        value: "line_cumulative",
        description: "Accumulating values over time as a continuous line",
      },
    ],
  },
  {
    label: "Total Value",
    options: [
      {
        label: "Number",
        value: "number",
        description: "A big number showing the total value",
      },
      {
        label: "Pie chart",
        value: "pie",
        description: "Proportions of a whole as a pie",
      },
      {
        label: "Bar chart (horizontal)",
        value: "bar_horizontal",
        description: "Total values as horizontal bars",
      },
    ],
  },
];

// Flat list of all chart type options (for backward compatibility)
export const CHART_TYPE_OPTIONS: ChartTypeOption[] = CHART_TYPE_GROUPS.flatMap(
  (group) => group.options
);

// Display mode groups including table view
export const DISPLAY_MODE_GROUPS: DisplayModeGroup[] = [
  {
    label: "Time Series",
    options: [
      {
        label: "Line chart",
        value: "line",
        description: "Trends over time as a continuous line",
      },
      {
        label: "Area chart",
        value: "area",
        description: "Trends over time as a shaded area",
      },
      {
        label: "Bar chart",
        value: "bar",
        description: "Trends over time as vertical bars side-by-side",
      },
      {
        label: "Stacked bar chart",
        value: "stacked_bar",
        description: "Trends over time as stacked vertical bars",
      },
    ],
  },
  {
    label: "Cumulative Time Series",
    options: [
      {
        label: "Line chart (cumulative)",
        value: "line_cumulative",
        description: "Accumulating values over time as a continuous line",
      },
    ],
  },
  {
    label: "Total Value",
    options: [
      {
        label: "Number",
        value: "number",
        description: "A big number showing the total value",
      },
      {
        label: "Pie chart",
        value: "pie",
        description: "Proportions of a whole as a pie",
      },
      {
        label: "Bar chart (horizontal)",
        value: "bar_horizontal",
        description: "Total values as horizontal bars",
      },
    ],
  },
  {
    label: "Data View",
    options: [
      {
        label: "Table",
        value: "table",
        description: "View data in a table format with rows and columns",
      },
    ],
  },
];

// Helper to check if chart type requires total/overall data (not time series)
export const isTotalValueChartType = (type: InsightType): boolean => {
  return type === "number" || type === "pie" || type === "bar_horizontal";
};

// Comparison is supported for all insight types except pie/horizontal bar.
// Number is included so KPI cards can show "vs previous period" and delta.
export const supportsComparisonForInsightType = (
  type: InsightType
): boolean => {
  return type !== "pie" && type !== "bar_horizontal";
};

// Time range preset options for dropdowns
export const TIME_RANGE_PRESET_OPTIONS: {
  label: string;
  value: TimeRangePreset;
}[] = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 14 days", value: "last_14_days" },
  { label: "Last 30 days", value: "last_30_days" },
  { label: "Last 90 days", value: "last_90_days" },
  { label: "This week", value: "this_week" },
  { label: "Last week", value: "last_week" },
  { label: "This month", value: "this_month" },
  { label: "Last month", value: "last_month" },
  { label: "Year to date", value: "year_to_date" },
];

// Comparison period options for dropdowns (matches PREVIOUS_DATE_RANGE_SELECT_OPTIONS without custom)
export const COMPARISON_PERIOD_OPTIONS: {
  label: string;
  value: ComparisonPeriod;
}[] = [
  { label: "No Comparison", value: "no_comparison" },
  { label: "Previous Period", value: "last_period" },
  { label: "1 Week Ago", value: "last_week" },
  { label: "1 Month Ago", value: "last_month" },
  { label: "1 Quarter Ago", value: "last_quarter" },
  { label: "1 Year Ago", value: "last_year" },
];

// Granularity options for dropdowns
export const GRANULARITY_OPTIONS: { label: string; value: Granularity }[] = [
  { label: "Day", value: "daily" },
  { label: "Week", value: "weekly" },
  { label: "Month", value: "monthly" },
];

// Y-axis unit options for dropdowns
export const Y_AXIS_UNIT_OPTIONS: { label: string; value: YAxisUnitType }[] = [
  { label: "None", value: "none" },
  { label: "Percentage", value: "percent_1" },
  { label: "Currency ($)", value: "currency" },
  { label: "Custom prefix...", value: "custom_prefix" },
  { label: "Custom postfix...", value: "custom_postfix" },
];

// Create a new empty insight with defaults
export function createDefaultInsight(
  existingInsightsCount: number = 0
): InsightConfig {
  return {
    id: crypto.randomUUID(),
    title: `New Insight ${existingInsightsCount + 1}`,
    series: [
      {
        id: crypto.randomUUID(),
        label: "A",
        metric: "customer_revenue",
        filters: [],
        color: SERIES_COLORS[0],
      },
    ],
    filters: [],
    filterOperator: "and",
    breakdown: [],
    timeRange: {
      preset: "last_7_days",
    },
    granularity: "daily",
    comparisonPeriod: "no_comparison",
    attributionPreset: "",
    type: "line",
    displayOptions: {
      showLegend: false,
      showMultipleYAxes: false,
      showSummaryStats: false,
      yAxisUnit: {
        type: "none",
        customValue: undefined,
      },
    },
    schemaVersion: 1,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

// Create a new series with next available label and color
export function createNewSeries(
  existingSeries: InsightSeries[]
): InsightSeries {
  const nextIndex = existingSeries.length;
  const nextLabel = SERIES_LABELS[nextIndex] || `#${nextIndex + 1}`;
  const nextColor = SERIES_COLORS[nextIndex % SERIES_COLORS.length];

  return {
    id: crypto.randomUUID(),
    label: nextLabel,
    metric: "customer_revenue",
    filters: [],
    color: nextColor,
  };
}

const CUSTOMER_TYPE_UNSUPPORTED_METRICS = new Set<InsightsMetricValue>([
  "total_spend",
  "click_through_rate",
  "cost_per_click",
  "cost_per_mille",
  "new_customer_purchase_ratio",
  "impressions",
  "clicks",
]);

const SINGLE_USE_FILTER_FIELDS = new Set<FilterableName>([
  "channel",
  "customer_type",
]);

export function supportsCustomerTypeForMetric(
  metric: InsightsMetricValue | string
): boolean {
  return !CUSTOMER_TYPE_UNSUPPORTED_METRICS.has(metric as InsightsMetricValue);
}

export function supportsCustomerTypeForAllSeries(
  series: InsightSeries[]
): boolean {
  return series.every((item) => supportsCustomerTypeForMetric(item.metric));
}

function shouldPreventDuplicateFilterField(
  field: FilterableName,
  availableFields: FilterableName[]
): boolean {
  return SINGLE_USE_FILTER_FIELDS.has(field) || availableFields.length === 1;
}

export function getCreatableFilterFields(
  filters: InsightFilterConfig[],
  disabledFields: FilterableName[] = []
): FilterableName[] {
  const availableFields = FILTER_FIELD_OPTIONS.map(
    (option) => option.value
  ).filter((field) => !disabledFields.includes(field));

  return availableFields.filter((field) => {
    if (!shouldPreventDuplicateFilterField(field, availableFields)) {
      return true;
    }

    return !filters.some((filter) => filter.name === field);
  });
}

export function isFilterFieldSelectionDisabled(
  field: FilterableName,
  filters: InsightFilterConfig[],
  disabledFields: FilterableName[] = [],
  currentFilterId?: string
): boolean {
  if (disabledFields.includes(field)) {
    return true;
  }

  const availableFields = FILTER_FIELD_OPTIONS.map(
    (option) => option.value
  ).filter((value) => !disabledFields.includes(value));

  if (!shouldPreventDuplicateFilterField(field, availableFields)) {
    return false;
  }

  return filters.some(
    (filter) => filter.name === field && filter.id !== currentFilterId
  );
}

export function removeCustomerTypeFilters(
  filters: InsightFilterConfig[] | undefined
): InsightFilterConfig[] {
  return (filters ?? []).filter((filter) => filter.name !== "customer_type");
}

export function sanitizeInsightCustomerTypeFilters(
  insight: InsightConfig
): InsightConfig {
  const sanitizedSeries = insight.series.map((series) => {
    if (supportsCustomerTypeForMetric(series.metric)) {
      return series;
    }

    const nextFilters = removeCustomerTypeFilters(series.filters);
    return nextFilters.length === (series.filters?.length ?? 0)
      ? series
      : { ...series, filters: nextFilters };
  });

  const nextFilters = supportsCustomerTypeForAllSeries(sanitizedSeries)
    ? insight.filters
    : removeCustomerTypeFilters(insight.filters);

  const hasSeriesChanges = sanitizedSeries.some(
    (series, index) => series !== insight.series[index]
  );
  const hasGlobalFilterChanges = nextFilters.length !== insight.filters.length;

  if (!hasSeriesChanges && !hasGlobalFilterChanges) {
    return insight;
  }

  return {
    ...insight,
    series: sanitizedSeries,
    filters: nextFilters,
  };
}

export function getChannelFilterFromInsight(insight: {
  filters?: InsightFilterConfig[];
}): string | null {
  return getChannelFilterFromFilters(insight.filters);
}

export function getChannelFilterFromFilters(
  filters: InsightFilterConfig[] | undefined
): string | null {
  const channelFilter = filters?.find((f) => f.name === "channel");
  if (!channelFilter) return null;

  if (channelFilter.operator === FilterTypesEnum.eq) {
    const value = String(channelFilter.value ?? "")
      .trim()
      .toLowerCase();
    return value || null;
  }

  if (channelFilter.operator === FilterTypesEnum.in) {
    const values = Array.isArray(channelFilter.value)
      ? channelFilter.value
      : String(channelFilter.value ?? "")
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);
    if (values.length === 1) {
      return String(values[0]).trim().toLowerCase() || null;
    }
  }

  return null;
}

export function getSeriesFilters(
  insightFilters: InsightFilterConfig[] | undefined,
  seriesFilters: InsightFilterConfig[] | undefined
): InsightFilterConfig[] {
  const baseFilters = getConfiguredInsightFilters(insightFilters);
  const localFilters = getConfiguredInsightFilters(seriesFilters);
  return [...baseFilters, ...localFilters];
}

export function getCustomerTypeFromInsightFilters(
  filters: InsightFilterConfig[] | undefined
): CustomerType | null {
  if (!filters || filters.length === 0) return null;
  const customerFilter = filters.find((f) => f.name === "customer_type");
  if (!customerFilter) return null;

  const rawValue = Array.isArray(customerFilter.value)
    ? customerFilter.value[0]
    : customerFilter.value;

  if (typeof rawValue !== "string") return null;
  const normalized = rawValue.trim().toLowerCase();
  if (
    normalized === "total" ||
    normalized === "new" ||
    normalized === "returning"
  ) {
    return normalized as CustomerType;
  }

  return null;
}

export function getSeriesCustomerType(
  filters: InsightFilterConfig[] | undefined,
  seriesFilters?: InsightFilterConfig[],
  metric?: InsightsMetricValue | string
): CustomerType {
  if (metric && !supportsCustomerTypeForMetric(metric)) {
    return "total";
  }

  return (
    getCustomerTypeFromInsightFilters(seriesFilters) ??
    getCustomerTypeFromInsightFilters(filters) ??
    "total"
  );
}

// Create default breakdown config
export function createDefaultBreakdown(): BreakdownConfig {
  return {
    dimension: "channel",
    limit: INSIGHT_BREAKDOWN_DEFAULT_LIMIT,
    sortBy: "customer_revenue",
    sortOrder: "desc",
  };
}

export function normalizeInsightSeries(
  series: InsightSeries[]
): InsightSeries[] {
  return series.map((item, index) => ({
    ...item,
    label: SERIES_LABELS[index] || `#${index + 1}`,
    color: SERIES_COLORS[index % SERIES_COLORS.length],
  }));
}

// Create a new filter
export function createNewFilter(
  name: FilterableName = "channel"
): InsightFilterConfig {
  const fieldOption = FILTER_FIELD_OPTIONS.find((f) => f.value === name);
  const defaultOperator = fieldOption?.operators[0] ?? FilterTypesEnum.eq;

  // Channel filters use array values (multi-select)
  const defaultValue = name === "channel" ? [] : "";

  return {
    id: crypto.randomUUID(),
    name,
    operator: defaultOperator,
    value: defaultValue,
  };
}

// All available filter fields
const GLOBAL_FILTER_FIELDS: FilterableName[] = ["customer_type"];
const BREAKDOWN_FILTER_FIELDS: FilterableName[] = [
  "channel",
  "campaign_id",
  "campaign_name",
  "adset_id",
  "adset_name",
  "ad_id",
  "ad_name",
];

// Filter field hierarchy by breakdown dimension.
// Defines which filter fields are ALLOWED for each breakdown type.
const ALLOWED_FILTER_FIELDS_BY_BREAKDOWN: Record<
  BreakdownDimension,
  FilterableName[]
> = {
  // Channel breakdown: only channel filter (can't filter by campaign/adset/ad at channel level)
  channel: ["channel"],
  // Campaign/AdSet/Ad breakdowns: all filters allowed (can filter by any level)
  campaign: BREAKDOWN_FILTER_FIELDS,
  adset: BREAKDOWN_FILTER_FIELDS,
  ad: BREAKDOWN_FILTER_FIELDS,
  // Creative breakdown: only channel + ad_name
  creative: ["channel", "ad_name"],
};

/**
 * Returns filter fields that should be disabled for channels that don't support them.
 * A filter is disabled only if ALL selected channels don't support that level.
 */
function getDisabledFilterFieldsForChannels(
  selectedChannels: string[] | null
): FilterableName[] {
  if (!selectedChannels || selectedChannels.length === 0) return [];

  const disabled: FilterableName[] = [];

  // Check campaign level - if ALL channels don't support campaigns
  const campaignChannels: readonly string[] =
    CHANNELS_SUPPORTING_BREAKDOWN.campaign;
  if (selectedChannels.every((ch) => !campaignChannels.includes(ch))) {
    disabled.push("campaign_id", "campaign_name");
  }

  // Check adset level - if ALL channels don't support adsets
  const adsetChannels: readonly string[] = CHANNELS_SUPPORTING_BREAKDOWN.adset;
  if (selectedChannels.every((ch) => !adsetChannels.includes(ch))) {
    disabled.push("adset_id", "adset_name");
  }

  // Check ad level - if ALL channels don't support ads
  const adChannels: readonly string[] = CHANNELS_SUPPORTING_BREAKDOWN.ad;
  if (selectedChannels.every((ch) => !adChannels.includes(ch))) {
    disabled.push("ad_id", "ad_name");
  }

  return disabled;
}

/**
 * Returns filter fields that should be disabled based on:
 * 1. Breakdown dimension hierarchy (from breakdown parameter)
 * 2. Channel capability (from selectedChannels parameter)
 *
 * A filter is disabled if EITHER:
 * - The breakdown hierarchy doesn't allow it, OR
 * - All selected channels don't support that filter level
 */
export function getDisabledFilterFields(
  breakdown: BreakdownConfig[] | BreakdownConfig | null,
  selectedChannels?: string[] | null
): FilterableName[] {
  // Normalize breakdown to first item if array
  const firstBreakdown = Array.isArray(breakdown) ? breakdown[0] : breakdown;

  // No breakdown = only global filters allowed
  if (!firstBreakdown) {
    return [...BREAKDOWN_FILTER_FIELDS];
  }

  // Breakdown hierarchy constraint
  const allowedByBreakdown =
    ALLOWED_FILTER_FIELDS_BY_BREAKDOWN[firstBreakdown.dimension] ?? [];
  const disabledByBreakdown = BREAKDOWN_FILTER_FIELDS.filter(
    (field) => !allowedByBreakdown.includes(field)
  );

  // Channel capability constraint
  const disabledByChannels = getDisabledFilterFieldsForChannels(
    selectedChannels ?? null
  );

  // Union of both constraints (disabled if either condition disables it)
  return [...new Set([...disabledByBreakdown, ...disabledByChannels])];
}

/**
 * Clean filters that are no longer valid for the given breakdown.
 * Call this when breakdown changes to remove orphaned filters.
 */
export function cleanFiltersForBreakdown(
  filters: InsightFilterConfig[],
  breakdown: BreakdownConfig[] | BreakdownConfig | null
): InsightFilterConfig[] {
  // Normalize breakdown to first item if array
  const firstBreakdown = Array.isArray(breakdown) ? breakdown[0] : breakdown;

  if (!firstBreakdown) {
    // No breakdown = keep only global filters
    return filters.filter((f) => GLOBAL_FILTER_FIELDS.includes(f.name));
  }

  const allowedFields =
    ALLOWED_FILTER_FIELDS_BY_BREAKDOWN[firstBreakdown.dimension] ?? [];
  return filters.filter(
    (f) =>
      GLOBAL_FILTER_FIELDS.includes(f.name) || allowedFields.includes(f.name)
  );
}

// ============================================================================
// Channel-Breakdown Constraint Functions
// ============================================================================
// These functions validate which breakdowns are supported for which channels
// and help filter queries to only supported channel/breakdown combinations.
// ============================================================================

/**
 * Extract channel values from insight filters.
 * Returns null if no channel filter is set (means all channels).
 * Returns array of channel values if filters exist.
 *
 * Note: Channel filters use only the "in" operator with multi-select UI.
 * Legacy "eq" operator is supported for backward compatibility.
 */
export function getChannelsFromInsightFilters(
  filters: InsightFilterConfig[] | undefined
): string[] | null {
  if (!filters || filters.length === 0) return null;

  const channelFilters = filters.filter((f) => f.name === "channel");
  if (channelFilters.length === 0) return null;

  const allChannelValues: string[] = [];

  for (const channelFilter of channelFilters) {
    if (
      channelFilter.value === "" ||
      channelFilter.value === null ||
      channelFilter.value === undefined
    ) {
      continue;
    }

    if (channelFilter.operator === FilterTypesEnum.eq) {
      const normalized = String(channelFilter.value).trim().toLowerCase();
      if (normalized) allChannelValues.push(normalized);
      continue;
    }

    // "in" operator (multi-select)
    if (channelFilter.operator === FilterTypesEnum.in) {
      const values = Array.isArray(channelFilter.value)
        ? channelFilter.value
        : String(channelFilter.value)
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);

      for (const v of values) {
        const normalized = String(v).trim().toLowerCase();
        if (normalized) allChannelValues.push(normalized);
      }
    }
  }

  if (allChannelValues.length === 0) return null;

  // Return unique values
  return [...new Set(allChannelValues)];
}

/**
 * Check if a breakdown dimension is valid for the given channels.
 * Returns true if all selected channels support the breakdown.
 */
export function isBreakdownValidForChannels(
  breakdown: BreakdownDimension | null,
  selectedChannels: string[] | null
): boolean {
  if (!breakdown) return true;
  if (!selectedChannels || selectedChannels.length === 0) return true;

  const supportedChannels = CHANNELS_SUPPORTING_BREAKDOWN[breakdown];
  if (supportedChannels === null) return true; // All channels supported

  return selectedChannels.every((channel) =>
    (supportedChannels as readonly string[]).includes(channel)
  );
}

/**
 * Get breakdown dimensions that should be disabled based on selected channels.
 * Returns array of breakdown dimensions that are not fully supported.
 */
export function getDisabledBreakdowns(
  selectedChannels: string[] | null
): BreakdownDimension[] {
  if (!selectedChannels || selectedChannels.length === 0) return [];

  const disabled: BreakdownDimension[] = [];

  for (const [breakdown, supportedChannels] of Object.entries(
    CHANNELS_SUPPORTING_BREAKDOWN
  )) {
    if (supportedChannels === null) continue; // All channels supported

    const allSupported = selectedChannels.every((channel) =>
      (supportedChannels as readonly string[]).includes(channel)
    );

    if (!allSupported) {
      disabled.push(breakdown as BreakdownDimension);
    }
  }

  return disabled;
}

/**
 * Get a user-friendly reason why a breakdown is disabled.
 * Returns null if the breakdown is not disabled.
 */
export function getBreakdownDisabledReason(
  breakdown: BreakdownDimension,
  selectedChannels: string[]
): string | null {
  const supportedChannels = CHANNELS_SUPPORTING_BREAKDOWN[breakdown];
  if (supportedChannels === null) return null;

  const unsupportedChannels = selectedChannels.filter(
    (channel) => !(supportedChannels as readonly string[]).includes(channel)
  );

  if (unsupportedChannels.length === 0) return null;

  const channelLabels = unsupportedChannels.map(
    (channel) =>
      TOUCHPOINT_OPTIONS.find((opt) => opt.value === channel)?.label ?? channel
  );

  if (channelLabels.length === 1) {
    return `Not available for ${channelLabels[0]}`;
  }

  return `Not available for ${channelLabels.slice(0, -1).join(", ")} and ${
    channelLabels[channelLabels.length - 1]
  }`;
}

/**
 * Filter channels to only those that support a given breakdown.
 * Used to prevent API calls for unsupported channel/breakdown combinations.
 */
export function getChannelsForBreakdown(
  breakdown: BreakdownDimension,
  requestedChannels: string[]
): string[] {
  const supportedChannels = CHANNELS_SUPPORTING_BREAKDOWN[breakdown];
  if (supportedChannels === null) return requestedChannels;

  return requestedChannels.filter((channel) =>
    (supportedChannels as readonly string[]).includes(channel)
  );
}

// ============================================================================
// Preset Inheritance Resolution Functions
// ============================================================================
// These functions handle resolving effective settings when insights can
// inherit from dashboard context or use their own settings.
// ============================================================================

/**
 * Time range context for inheritance resolution.
 * Used when insight is displayed within a dashboard.
 */
export interface DashboardTimeRangeContext {
  preset: TimeRangePreset;
  customDays?: number;
}

/**
 * Resolve the effective time range for an insight.
 *
 * Rules:
 * - If dashboardContext is null/undefined: standalone mode, always use insight's timeRange
 * - If insight.useOwnTimeRange is true: use insight's timeRange
 * - Otherwise: inherit from dashboardContext
 */
export function getEffectiveTimeRange(
  insight: Pick<InsightConfig, "timeRange" | "useOwnTimeRange">,
  dashboardContext: DashboardTimeRangeContext | null | undefined
): InsightConfig["timeRange"] {
  // Standalone mode: always use insight's own time range
  if (!dashboardContext) {
    return insight.timeRange;
  }

  // Dashboard mode: check if insight overrides
  if (insight.useOwnTimeRange) {
    return insight.timeRange;
  }

  // Inherit from dashboard
  return {
    preset: dashboardContext.preset,
    customDays: dashboardContext.customDays,
  };
}

/**
 * Resolve the effective attribution preset for an insight.
 *
 * Rules:
 * - If insight.attributionPreset is non-empty: use it
 * - Otherwise: inherit from dashboardPreset (or URL state in standalone)
 */
export function getEffectiveAttributionPreset(
  insight: Pick<InsightConfig, "attributionPreset">,
  dashboardPreset: string | null | undefined
): string {
  if (insight.attributionPreset.trim().length > 0) {
    return insight.attributionPreset;
  }

  return dashboardPreset ?? "";
}

/**
 * Check if an insight is using custom settings that differ from dashboard defaults.
 * Returns an object indicating which settings are overridden.
 */
export function getInsightOverrideStatus(
  insight: Pick<InsightConfig, "useOwnTimeRange" | "attributionPreset">
): {
  hasTimeRangeOverride: boolean;
  hasAttributionOverride: boolean;
  hasAnyOverride: boolean;
} {
  const hasTimeRangeOverride = insight.useOwnTimeRange === true;
  const hasAttributionOverride = insight.attributionPreset.trim().length > 0;

  return {
    hasTimeRangeOverride,
    hasAttributionOverride,
    hasAnyOverride: hasTimeRangeOverride || hasAttributionOverride,
  };
}
